import { useMemo, useCallback, useEffect, useState, memo } from 'react';
import { useSelector } from '@xstate/store/react';
import { CanvasCanvas } from './CanvasCanvas';
import { CanvasToolbar } from './CanvasToolbar';
import { CanvasSidebar } from './CanvasSidebar';
import { CanvasTaskPicker } from './CanvasTaskPicker';
import { CanvasGroupPicker } from './CanvasGroupPicker';
import { ContextMenu } from '../../components/ContextMenu';
import { CanvasProvider, useCanvasStores, useCanvasRotation, useCanvasGroups } from '../stores/CanvasContext';
import { useExistingTaskIds } from '../hooks/useExistingTaskIds';
import type { SpriteCatalogEntry } from '../sprites/spriteTypes';
import { useCanvasContextMenus } from '../hooks/useCanvasContextMenus';
import { useCanvasActions } from '../hooks/useCanvasActions';
import { CanvasInteractionSetup } from './CanvasInteractionSetup';
import { AdrDetailSidebar } from './AdrDetailSidebar';
import { ResourceDetailSidebar } from './ResourceDetailSidebar';
import { initCanvasStore } from '../stores/canvasStore';
import { GRID_SIZE, screenToCanvas, findEntityAtPoint } from '../types';
import type { CanvasPattern } from '../stores/canvasUiStore';
import type { CanvasEntity } from '../entityTypes';
import { CanvasDebugToolbar } from './CanvasDebugToolbar';

function getPatternCSS(pattern: CanvasPattern, gridPx: number): { backgroundImage: string; backgroundSize: string } {
  switch (pattern) {
    case 'none':
      return { backgroundImage: 'none', backgroundSize: `${gridPx}px ${gridPx}px` };
    case 'dots':
      return {
        backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
        backgroundSize: `${gridPx}px ${gridPx}px`,
      };
    case 'grid':
      return {
        backgroundImage:
          `linear-gradient(to right, #e5e7eb 1px, transparent 1px),` +
          `linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)`,
        backgroundSize: `${gridPx}px ${gridPx}px`,
      };
    case 'crosses': {
      const arm = Math.max(1, Math.round(gridPx * 0.3));
      const half = gridPx / 2;
      const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='${gridPx}' height='${gridPx}'>` +
        `<line x1='${half - arm}' y1='${half}' x2='${half + arm}' y2='${half}' stroke='%23d1d5db' stroke-width='1'/>` +
        `<line x1='${half}' y1='${half - arm}' x2='${half}' y2='${half + arm}' stroke='%23d1d5db' stroke-width='1'/>` +
        `</svg>`;
      return {
        backgroundImage: `url("data:image/svg+xml,${svg}")`,
        backgroundSize: `${gridPx}px ${gridPx}px`,
      };
    }
    case 'graph-paper': {
      const majorPx = gridPx * 5;
      return {
        backgroundImage:
          `linear-gradient(to right, #d1d5db 1px, transparent 1px),` +
          `linear-gradient(to bottom, #d1d5db 1px, transparent 1px),` +
          `linear-gradient(to right, #e5e7eb 1px, transparent 1px),` +
          `linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)`,
        backgroundSize: `${majorPx}px ${majorPx}px,${majorPx}px ${majorPx}px,${gridPx}px ${gridPx}px,${gridPx}px ${gridPx}px`,
      };
    }
  }
}

export interface CanvasViewProps {
  boardId: string;
}

export function CanvasView({ boardId }: CanvasViewProps) {
  const { store, txManager } = useMemo(() => {
    return initCanvasStore(boardId);
  }, [boardId]);

  return (
    <CanvasProvider store={store} txManager={txManager} boardId={boardId}>
      <CanvasInteractionSetup />
      <CanvasViewInner />
    </CanvasProvider>
  );
}

// Isolated component: updates grid background via direct DOM manipulation
// (no React re-renders on scroll/zoom).
const CanvasContainer = memo(function CanvasContainer({ children, setResourceDropTargetId }: { children: React.ReactNode; setResourceDropTargetId: (id: string | null) => void }) {
  const { store, panZoomStore, uiStore, containerRef } = useCanvasStores();
  const rotation = useCanvasRotation();
  const { handleCanvasContextMenu } = useCanvasContextMenus();
  const { handleAddSpriteAtScreen, handleAddImage, handleAddAdrTrigger, handleAttachResource } = useCanvasActions();

  /** Convert screen event coords to canvas space and find entity at that point. */
  const findEntityUnderCursor = useCallback((screenX: number, screenY: number): CanvasEntity | null => {
    const container = containerRef.current;
    if (!container) return null;
    const pz = panZoomStore.getSnapshot().context;
    const canvasPos = screenToCanvas(screenX, screenY, container, pz, rotation);
    const nodes = Object.values(store.getSnapshot().context.nodes);
    return findEntityAtPoint(canvasPos, nodes);
  }, [containerRef, panZoomStore, store, rotation]);

  // Ref-based background grid update — bypasses React render on every scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function update() {
      const { panX, panY, zoom } = panZoomStore.getSnapshot().context;
      const pattern = uiStore.getSnapshot().context.canvasPattern;
      const gridPx = GRID_SIZE * zoom;
      const css = getPatternCSS(pattern, gridPx);
      el!.style.backgroundImage = css.backgroundImage;
      el!.style.backgroundSize = css.backgroundSize;
      if (pattern === 'graph-paper') {
        const majorPx = gridPx * 5;
        const majX = `${panX % majorPx}px`;
        const majY = `${panY % majorPx}px`;
        const minX = `${panX % gridPx}px`;
        const minY = `${panY % gridPx}px`;
        el!.style.backgroundPosition = `${majX} ${majY},${majX} ${majY},${minX} ${minY},${minX} ${minY}`;
      } else {
        el!.style.backgroundPosition = `${panX % gridPx}px ${panY % gridPx}px`;
      }
    }
    update();
    const sub1 = panZoomStore.subscribe(update);
    const sub2 = uiStore.subscribe(update);
    return () => { sub1.unsubscribe(); sub2.unsubscribe(); };
  }, [panZoomStore, uiStore, containerRef]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/x-canvas-sprite')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      return;
    }
    if (e.dataTransfer.types.includes('application/x-canvas-adr-trigger')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      return;
    }
    if (e.dataTransfer.types.includes('application/x-canvas-resource-badge')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      const target = findEntityUnderCursor(e.clientX, e.clientY);
      setResourceDropTargetId(target?.id ?? null);
      return;
    }
    // Accept image files dragged from desktop
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [findEntityUnderCursor, setResourceDropTargetId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    // Sprite drop from panel
    const spriteData = e.dataTransfer.getData('application/x-canvas-sprite');
    if (spriteData) {
      e.preventDefault();
      try {
        const entry = JSON.parse(spriteData) as SpriteCatalogEntry;
        handleAddSpriteAtScreen(entry, e.clientX, e.clientY);
      } catch { /* ignore invalid data */ }
      return;
    }
    // ADR trigger drop from sidebar
    const adrData = e.dataTransfer.getData('application/x-canvas-adr-trigger');
    if (adrData) {
      e.preventDefault();
      try {
        const parsed = JSON.parse(adrData) as { adrIds: string[] };
        handleAddAdrTrigger(parsed.adrIds, undefined, { x: e.clientX, y: e.clientY });
      } catch { /* ignore invalid data */ }
      return;
    }
    // Resource drop from sidebar → attach to entity under cursor
    const resourceData = e.dataTransfer.getData('application/x-canvas-resource-badge');
    if (resourceData) {
      e.preventDefault();
      setResourceDropTargetId(null);
      try {
        const parsed = JSON.parse(resourceData) as { resourceId: string };
        const target = findEntityUnderCursor(e.clientX, e.clientY);
        if (target) handleAttachResource(target.id, parsed.resourceId);
      } catch { /* ignore invalid data */ }
      return;
    }
    // Image file drop from desktop
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith('image/')) return;
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const img = new window.Image();
        img.onload = () => {
          handleAddImage(dataUrl, img.naturalWidth, img.naturalHeight);
        };
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    }
  }, [handleAddSpriteAtScreen, handleAddImage, handleAddAdrTrigger, handleAttachResource, findEntityUnderCursor, setResourceDropTargetId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setResourceDropTargetId(null);
    }
  }, [containerRef, setResourceDropTargetId]);

  return (
    <div
      ref={containerRef}
      className="relative flex-1 overflow-hidden"
      style={{
        height: '100%',
        backgroundColor: '#f9fafb',
      }}
      onContextMenu={(e) => handleCanvasContextMenu(e)}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      {children}
    </div>
  );
});

function CanvasViewInner() {
  const { uiStore } = useCanvasStores();
  const [resourceDropTargetId, setResourceDropTargetId] = useState<string | null>(null);

  const addPickerOpen = useSelector(uiStore, s => s.context.addPickerState !== null);
  const contextMenu = useSelector(uiStore, s => s.context.contextMenu);
  const groupPickerOpen = useSelector(uiStore, s => s.context.groupPickerState !== null);

  return (
    <div className="flex w-full" style={{ height: '100vh' }}>
      <CanvasSidebar />
      <CanvasContainer setResourceDropTargetId={setResourceDropTargetId}>
        <CanvasCanvas resourceDropTargetId={resourceDropTargetId} />

        <CanvasToolbar />
        <CanvasDebugToolbar />

        {/* Pickers mount only when open → no materialized subscription during drag */}
        {addPickerOpen && <TaskPickerOverlay />}
        {groupPickerOpen && <GroupPickerOverlay />}

        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
            onClose={() => uiStore.send({ type: 'contextMenu.set', menu: null })}
          />
        )}
      </CanvasContainer>
      <AdrDetailSidebar />
      <ResourceDetailSidebar />
    </div>
  );
}

// Conditionally-rendered picker wrappers: subscribe to materialized data
// only when open (never during drag).

function TaskPickerOverlay() {
  const { uiStore } = useCanvasStores();
  const addPickerState = useSelector(uiStore, s => s.context.addPickerState);
  const existingTaskIds = useExistingTaskIds();
  const { handleAddTask } = useCanvasActions();

  return (
    <CanvasTaskPicker
      isOpen={addPickerState !== null}
      onClose={() => uiStore.send({ type: 'addPicker.set', state: null })}
      onSelect={(taskId) => {
        handleAddTask(taskId, addPickerState?.layerId, addPickerState?.screenPos);
        uiStore.send({ type: 'addPicker.set', state: null });
      }}
      existingTaskIds={existingTaskIds}
    />
  );
}

function GroupPickerOverlay() {
  const { store, uiStore } = useCanvasStores();
  const groups = useCanvasGroups();
  const groupPickerState = useSelector(uiStore, s => s.context.groupPickerState);

  // Only show groups on the same layer as the target entities
  const sameLayerGroups = useMemo(() => {
    if (!groupPickerState) return groups;
    const ctx = store.getSnapshot().context;
    const allEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.groups),
    ];
    const entityMap = new Map(allEntities.map(e => [e.id, e]));
    const targetLayers = new Set<string>();
    for (const nid of groupPickerState.targetNodeIds) {
      const e = entityMap.get(nid);
      if (e) targetLayers.add(e.layerId);
    }
    if (targetLayers.size !== 1) return [];
    const targetLayerId = [...targetLayers][0];
    return groups.filter(g => g.layerId === targetLayerId);
  }, [groups, groupPickerState, store]);

  const groupPickerCurrentGroupIds = useMemo(() => {
    if (!groupPickerState) return new Set<string>();
    const ids = new Set<string>();
    for (const g of sameLayerGroups) {
      if (groupPickerState.targetNodeIds.some((nid: string) => g.memberNodeIds.includes(nid))) {
        ids.add(g.id);
      }
    }
    return ids;
  }, [sameLayerGroups, groupPickerState]);

  return (
    <CanvasGroupPicker
      isOpen={groupPickerState !== null}
      onClose={() => uiStore.send({ type: 'groupPicker.set', state: null })}
      groups={sameLayerGroups}
      currentGroupIds={groupPickerCurrentGroupIds}
      onSelect={(groupId) => {
        if (!groupPickerState) return;
        store.send({ type: 'group.assignEntities', groupId, entityIds: groupPickerState.targetNodeIds });
        uiStore.send({ type: 'groupPicker.set', state: null });
      }}
    />
  );
}
